import { useEffect, useState } from "react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Send, Loader2, Eye, CheckCircle2, XCircle, History, Copy, Mail,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

/**
 * /admin/integrations/finbpm — nudge Kevin (or any integrator) about the
 * partner API key drop. Renders an HTML email preview, sends via the
 * system SMTP, and keeps an audit trail.
 */
const DEFAULT_RECIPIENT = "kevin@tryant.ai";
const DEFAULT_CC = "management@finbpm.com";

const FinbpmNudgePage = () => {
  const [toEmail, setToEmail] = useState(DEFAULT_RECIPIENT);
  const [toName, setToName] = useState("Kevin");
  const [ccEmails, setCcEmails] = useState(DEFAULT_CC);
  const [senderName, setSenderName] = useState("EchoSense Capital Club Engineering");
  const [customNote, setCustomNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);

  // Comma/semicolon split → cleaned list. Empty input → [] (means "no CC").
  const parseCcList = (raw) =>
    (raw || "")
      .split(/[,;]/)
      .map((s) => s.trim())
      .filter(Boolean);

  const loadHistory = async () => {
    try {
      const r = await api.get("/admin/integrations/finbpm/history");
      setHistory(r.data.history || []);
    } catch (e) {
      console.error("history fetch failed", e);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const runPreview = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/integrations/finbpm/nudge", {
        to_email: toEmail,
        to_name: toName,
        cc_emails: parseCcList(ccEmails),
        sender_name: senderName,
        custom_note: customNote || null,
        dry_run: true,
      });
      setPreview(r.data);
      toast.success("Preview generated.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed.");
    } finally {
      setBusy(false);
    }
  };

  const fireSend = async () => {
    const ccPretty = parseCcList(ccEmails).join(", ") || "(no CC)";
    if (!window.confirm(`Send the partner nudge to ${toEmail}?\nCC: ${ccPretty}`)) return;
    setSending(true);
    try {
      const r = await api.post("/admin/integrations/finbpm/nudge", {
        to_email: toEmail,
        to_name: toName,
        cc_emails: parseCcList(ccEmails),
        sender_name: senderName,
        custom_note: customNote || null,
        dry_run: false,
      });
      setPreview(r.data);
      if (r.data.delivered) {
        toast.success(`Sent to ${r.data.recipient}.`);
      } else {
        toast.warning(r.data.reason || "Send failed — use copy-paste fallback.");
      }
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  const copyHtml = () => {
    if (!preview?.html) return;
    navigator.clipboard.writeText(preview.html);
    toast.success("HTML copied to clipboard.");
  };

  const copyText = () => {
    if (!preview?.text) return;
    navigator.clipboard.writeText(preview.text);
    toast.success("Plain-text copied to clipboard.");
  };

  const live = preview?.live_state;

  return (
    <DashboardLayout
      title="Partner Integration — Integrator Nudge"
      subtitle="Send Kevin (or any external integrator) a status nudge with the live state of the integration baked in."
    >
      <div className="space-y-6" data-testid="finbpm-nudge-page">
        {/* Compose card */}
        <Card className="border-platinum/10 bg-navy-light/30">
          <CardHeader>
            <CardTitle className="text-platinum text-base font-serif flex items-center gap-2">
              <Mail size={16} className="text-gold" /> Compose nudge
            </CardTitle>
            <CardDescription className="text-platinum/60">
              The email body auto-includes live counts (handoff acknowledgements, pending funding requests)
              and whether Paytiko keys are present in this environment.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="finbpm-to-email" className="text-platinum/80 text-xs uppercase tracking-wider">
                  Recipient email
                </Label>
                <Input
                  id="finbpm-to-email"
                  data-testid="finbpm-nudge-to-email"
                  value={toEmail}
                  onChange={(e) => setToEmail(e.target.value)}
                  className="bg-obsidian/50 border-platinum/20 text-platinum"
                />
              </div>
              <div>
                <Label htmlFor="finbpm-to-name" className="text-platinum/80 text-xs uppercase tracking-wider">
                  Recipient first name
                </Label>
                <Input
                  id="finbpm-to-name"
                  data-testid="finbpm-nudge-to-name"
                  value={toName}
                  onChange={(e) => setToName(e.target.value)}
                  className="bg-obsidian/50 border-platinum/20 text-platinum"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="finbpm-cc" className="text-platinum/80 text-xs uppercase tracking-wider">
                  CC (comma-separated · leave blank for no CC)
                </Label>
                <Input
                  id="finbpm-cc"
                  data-testid="finbpm-nudge-cc"
                  value={ccEmails}
                  onChange={(e) => setCcEmails(e.target.value)}
                  placeholder="management@finbpm.com"
                  className="bg-obsidian/50 border-platinum/20 text-platinum"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="finbpm-sender" className="text-platinum/80 text-xs uppercase tracking-wider">
                  Signed by
                </Label>
                <Input
                  id="finbpm-sender"
                  data-testid="finbpm-nudge-sender"
                  value={senderName}
                  onChange={(e) => setSenderName(e.target.value)}
                  className="bg-obsidian/50 border-platinum/20 text-platinum"
                />
              </div>
              <div className="md:col-span-2">
                <Label htmlFor="finbpm-note" className="text-platinum/80 text-xs uppercase tracking-wider">
                  Optional personal note (appended after the body)
                </Label>
                <Textarea
                  id="finbpm-note"
                  data-testid="finbpm-nudge-note"
                  value={customNote}
                  onChange={(e) => setCustomNote(e.target.value)}
                  rows={3}
                  placeholder="e.g. Hey Kevin — also let me know if you need access to the partner sandbox docs."
                  className="bg-obsidian/50 border-platinum/20 text-platinum"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 pt-2">
              <Button
                onClick={runPreview}
                disabled={busy}
                variant="outline"
                className="border-platinum/30 text-platinum hover:bg-platinum/10"
                data-testid="finbpm-nudge-preview-btn"
              >
                {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Eye size={14} className="mr-2" />}
                Preview
              </Button>
              <Button
                onClick={fireSend}
                disabled={sending}
                className="bg-gold hover:bg-gold/90 text-navy-dark font-semibold"
                data-testid="finbpm-nudge-send-btn"
              >
                {sending ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                Fire off email
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Live state badges */}
        {live && (
          <Card className="border-platinum/10 bg-navy-light/30">
            <CardContent className="p-5 flex flex-wrap gap-3" data-testid="finbpm-nudge-live-state">
              <Badge className={`text-xs ${live.paytiko_configured ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"} border`}>
                {live.paytiko_configured ? "Paytiko keys LIVE" : "Paytiko keys MISSING"}
              </Badge>
              <Badge className="bg-platinum/10 text-platinum/80 border border-platinum/20 text-xs">
                Handoffs signed: {live.handoff_acknowledgements}
              </Badge>
              <Badge className="bg-platinum/10 text-platinum/80 border border-platinum/20 text-xs">
                Pending funding requests: {live.pending_funding_requests}
              </Badge>
            </CardContent>
          </Card>
        )}

        {/* Send result banner */}
        {preview && preview.delivered === true && !preview.dry_run && (
          <Card className="border-emerald-500/40 bg-emerald-500/[0.06] border-2" data-testid="finbpm-nudge-result-success">
            <CardContent className="p-5 flex items-start gap-3">
              <CheckCircle2 className="text-emerald-300 shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-emerald-200 font-semibold">Delivered to {preview.recipient}</p>
                {preview.cc && preview.cc.length > 0 && (
                  <p className="text-xs text-emerald-200/80 mt-1">CC: {preview.cc.join(", ")}</p>
                )}
                <p className="text-sm text-platinum/70 mt-1">{preview.subject}</p>
              </div>
            </CardContent>
          </Card>
        )}
        {preview && preview.delivered === false && !preview.dry_run && (
          <Card className="border-amber-400/40 bg-amber-400/[0.06] border-2" data-testid="finbpm-nudge-result-fallback">
            <CardContent className="p-5 flex items-start gap-3">
              <XCircle className="text-amber-300 shrink-0 mt-0.5" size={20} />
              <div>
                <p className="text-amber-200 font-semibold">SMTP delivery failed — use copy-paste below</p>
                <p className="text-sm text-platinum/70 mt-1">
                  {preview.reason || "SMTP isn't configured in this environment."}
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Preview render */}
        {preview && (
          <Card className="border-platinum/10 bg-navy-light/30">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div>
                <CardTitle className="text-platinum text-base font-serif">Email preview</CardTitle>
                <CardDescription className="text-platinum/60">
                  Subject: <span className="text-platinum/90">{preview.subject}</span>
                </CardDescription>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={copyText}
                  className="border-platinum/30 text-platinum hover:bg-platinum/10"
                  data-testid="finbpm-nudge-copy-text">
                  <Copy size={12} className="mr-1.5" /> Plain
                </Button>
                <Button size="sm" variant="outline" onClick={copyHtml}
                  className="border-platinum/30 text-platinum hover:bg-platinum/10"
                  data-testid="finbpm-nudge-copy-html">
                  <Copy size={12} className="mr-1.5" /> HTML
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border border-platinum/10 overflow-hidden bg-white">
                <iframe
                  title="Email preview"
                  srcDoc={preview.html}
                  className="w-full h-[640px] border-0"
                  sandbox=""
                  data-testid="finbpm-nudge-preview-iframe"
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* Outreach history */}
        <Card className="border-platinum/10 bg-navy-light/30">
          <CardHeader>
            <CardTitle className="text-platinum text-base font-serif flex items-center gap-2">
              <History size={16} className="text-gold" /> Outreach history
            </CardTitle>
            <CardDescription className="text-platinum/60">
              Audit log of every nudge fired from this admin panel.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <p className="text-platinum/50 text-sm italic">No nudges sent yet.</p>
            ) : (
              <div className="space-y-2">
                {history.map((row) => (
                  <div
                    key={`${row.sent_at}-${row.recipient}`}
                    className="flex items-center justify-between gap-3 rounded-md border border-platinum/10 bg-obsidian/40 p-3"
                    data-testid={`finbpm-nudge-history-${row.sent_at}`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm text-platinum/90 truncate">
                        {row.recipient}
                        {row.cc && row.cc.length > 0 && (
                          <span className="text-platinum/50 text-xs"> · CC: {row.cc.join(", ")}</span>
                        )}
                      </p>
                      <p className="text-xs text-platinum/50">
                        {new Date(row.sent_at).toLocaleString()} · sent by {row.sent_by || "—"}
                      </p>
                    </div>
                    <Badge className={`text-[10px] ${row.delivered ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-red-500/15 text-red-300 border-red-500/30"} border`}>
                      {row.delivered ? "Delivered" : "Failed"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default FinbpmNudgePage;
